import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { BarChart3 } from 'lucide-react';
import { cn } from '@/lib/utils';
import AbsenceSection from './AbsenceSection';
import EngagementSection from './EngagementSection';
import OtherMetricsSection from './OtherMetricsSection';
import FinanceSection from './FinanceSection';

/** URL hash (without #) to tab value */
const HASH_TO_TAB = {
  nieobecnosci: 'absences',
  zaangazowanie: 'engagement',
  finanse: 'finance',
  inne: 'other'
};

/** Tab value to URL hash */
const TAB_TO_HASH = {
  absences: 'nieobecnosci',
  engagement: 'zaangazowanie',
  finance: 'finanse',
  other: 'inne'
};

function getHash() {
  const raw = typeof window !== 'undefined' ? window.location.hash.slice(1).toLowerCase() : '';
  return raw || null;
}

function tabFromHash(hash, availableTabs) {
  const tab = hash ? HASH_TO_TAB[hash] : null;
  if (tab && availableTabs.includes(tab)) return tab;
  return availableTabs[0];
}

/**
 * Main Analytics Dashboard Component
 * Pill switcher for Absences, Engagement, Finance (optional), Other Metrics. URL hash synced (e.g. #finanse).
 * @param {boolean} isJadlospisEnabled - When false, Finanse section is hidden (per branch module toggle)
 * @param {boolean} isMediaEnabled - When false, Nowe zdjęcia and Nowe reakcje rodziców in Engagement are hidden
 */
export default function AnalyticsDashboard({ branchId, isBranchAdmin = true, isJadlospisEnabled = true, isMediaEnabled = true }) {
  const availableTabs = useMemo(() => {
    const tabs = ['absences', 'engagement'];
    if (isBranchAdmin && isJadlospisEnabled) tabs.push('finance');
    tabs.push('other');
    return tabs;
  }, [isBranchAdmin, isJadlospisEnabled]);

  const [activeTab, setActiveTab] = useState(() =>
    tabFromHash(getHash(), availableTabs)
  );

  // Keep activeTab valid when availableTabs change (e.g. finance disabled)
  useEffect(() => {
    if (!availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]);
    }
  }, [availableTabs, activeTab]);

  // Sync tab -> URL hash when user switches tab
  const handleTabChange = (value) => {
    setActiveTab(value);
    const hash = TAB_TO_HASH[value];
    if (typeof window !== 'undefined' && hash) {
      const url = `${window.location.pathname}${window.location.search}#${hash}`;
      window.history.replaceState(null, '', url);
    }
  };

  // Sync URL hash -> tab (e.g. browser back/forward or external link)
  useEffect(() => {
    const syncFromHash = () => {
      const tab = tabFromHash(getHash(), availableTabs);
      setActiveTab(tab);
    };
    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, [availableTabs]);

  const pillLabels = {
    absences: 'Nieobecności',
    engagement: 'Zaangażowanie',
    finance: 'Finanse',
    other: 'Inne metryki'
  };

  const tabsListRef = useRef(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0, top: 0, height: 0 });
  const [indicatorReady, setIndicatorReady] = useState(false);

  useLayoutEffect(() => {
    const list = tabsListRef.current;
    if (!list) return;
    const activeTrigger = list.querySelector('[data-state=active]');
    if (!activeTrigger) return;
    const listRect = list.getBoundingClientRect();
    const triggerRect = activeTrigger.getBoundingClientRect();
    setIndicator({
      left: triggerRect.left - listRect.left,
      width: triggerRect.width,
      top: triggerRect.top - listRect.top,
      height: triggerRect.height
    });
    setIndicatorReady(true);
  }, [activeTab]);

  useEffect(() => {
    const list = tabsListRef.current;
    if (!list) return;
    const ro = new ResizeObserver(() => {
      const activeTrigger = list.querySelector('[data-state=active]');
      if (!activeTrigger) return;
      const listRect = list.getBoundingClientRect();
      const triggerRect = activeTrigger.getBoundingClientRect();
      setIndicator({
        left: triggerRect.left - listRect.left,
        width: triggerRect.width,
        top: triggerRect.top - listRect.top,
        height: triggerRect.height
      });
    });
    ro.observe(list);
    return () => ro.disconnect();
  }, [activeTab]);

  return (
    <div className="space-y-6">
      {/* Dashboard Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-indigo-100 rounded-lg">
          <BarChart3 className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Analityka</h1>
          <p className="text-sm text-slate-500">Szczegółowe statystyki i trendy</p>
        </div>
      </div>

      {/* Pill switcher + content */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="relative">
          <TabsList
            ref={tabsListRef}
            className={cn(
              'w-fit max-w-full inline-flex h-auto flex-nowrap gap-1 rounded-full bg-slate-100 p-1 text-slate-600 relative'
            )}
          >
            {availableTabs.map((tabId) => (
              <TabsTrigger
                key={tabId}
                value={tabId}
                className={cn(
                  'rounded-full px-4 py-2 text-sm font-medium transition-colors relative z-[1]',
                  'data-[state=active]:text-slate-900 data-[state=active]:shadow-none',
                  indicatorReady ? 'data-[state=active]:bg-transparent' : 'data-[state=active]:bg-white data-[state=active]:shadow-sm'
                )}
              >
                {pillLabels[tabId]}
              </TabsTrigger>
            ))}
          </TabsList>
          {/* Sliding pill indicator */}
          {indicatorReady && (
            <div
              className="absolute rounded-full bg-white shadow-sm pointer-events-none z-0 transition-[left,width,top,height] duration-200 ease-out"
              style={{
                left: indicator.left,
                width: indicator.width,
                top: indicator.top,
                height: indicator.height
              }}
              aria-hidden
            />
          )}
        </div>

        <TabsContent value="absences" className="mt-4 focus-visible:outline-none">
          <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
            <div className="p-6">
              <AbsenceSection branchId={branchId} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="engagement" className="mt-4 focus-visible:outline-none">
          <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
            <div className="p-6">
              <EngagementSection
                branchId={branchId}
                isBranchAdmin={isBranchAdmin}
                isMediaEnabled={isMediaEnabled}
              />
            </div>
          </div>
        </TabsContent>

        {isBranchAdmin && isJadlospisEnabled && (
          <TabsContent value="finance" className="mt-4 focus-visible:outline-none">
            <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
              <div className="p-6">
                <FinanceSection branchId={branchId} />
              </div>
            </div>
          </TabsContent>
        )}

        <TabsContent value="other" className="mt-4 focus-visible:outline-none">
          <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
            <div className="p-6">
              <OtherMetricsSection branchId={branchId} />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
